import * as React from 'react'
import { useEffect } from 'react'
import {
	Menu,
	Paper,
	Box,
	Divider,
	Badge,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Typography,
} from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import PendingIcon from '@mui/icons-material/Pending'
import RefreshIcon from '@mui/icons-material/Refresh'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import { useIndexedDB } from '../hooks/useIndexedDB'
import { useLiveQuery } from 'dexie-react-hooks'
import dayjs from 'dayjs'
import { useConnectionContext } from './ConnectionContext'
import { syncWithBackend } from '../../services/OfflineServices/foremanCacheService'

interface SyncMenuProps {
	showAccountInfo: boolean
	projectTimeZone?: string
}

export default function SyncMenu({ showAccountInfo, projectTimeZone }: SyncMenuProps) {
	const [syncAnchorEl, setSyncAnchorEl] = React.useState<null | HTMLElement>(null)
	const db = useIndexedDB()
	const mutations = db ? useLiveQuery(() => db.mutations.toArray()) : []
	const { connectionStatus } = useConnectionContext()
	// Use useLiveQuery to reactively track sync state from the database table
	const syncInProgress = db
		? useLiveQuery(() => db.syncState.get(1).then((state) => state?.syncInProgress ?? false))
		: false
	const syncMenuOpen = Boolean(syncAnchorEl)
	const handleSyncClick = (event: React.MouseEvent<HTMLElement>) => {
		setSyncAnchorEl(event.currentTarget)
	}

	const handleSyncClose = () => {
		setSyncAnchorEl(null)
	}

	if (!showAccountInfo) {
		return null
	}

	useEffect(() => {
		if (connectionStatus === 'online' && db) {
			const sync = async () => {
				await syncWithBackend(db)
			}
			sync()
		}
	}, [connectionStatus, db])

	return (
		<>
			{/* Sync Menu */}
			<div
				onClick={handleSyncClick}
				style={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'pointer',
					marginLeft: '10px',
				}}
			>
				<Badge
					badgeContent={mutations?.length ?? 0}
					color='warning'
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					overlap='circular'
					sx={{
						'& .MuiBadge-badge': {
							left: 0,
							top: '50%',
							transform: 'translate(-50%, -50%)',
						},
					}}
				>
					<SyncIcon
						sx={{
							animation: syncInProgress ? 'spin 1s linear infinite' : 'none',
							'@keyframes spin': {
								'0%': {
									transform: 'rotate(0deg)',
								},
								'100%': {
									transform: 'rotate(-360deg)',
								},
							},
						}}
					/>
				</Badge>
			</div>
			<Menu
				anchorEl={syncAnchorEl}
				open={syncMenuOpen}
				onClose={handleSyncClose}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				PaperProps={{
					elevation: 0,
					sx: {
						overflow: 'visible',
						filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
						mt: 1.5,
						'&:before': {
							content: '""',
							display: 'block',
							position: 'absolute',
							top: 0,
							right: 14,
							width: 10,
							height: 10,
							bgcolor: 'background.paper',
							transform: 'translateY(-50%) rotate(45deg)',
							zIndex: 0,
						},
					},
				}}
			>
				<Paper sx={{ width: 320, maxWidth: '100%' }}>
					<Box
						sx={{
							p: 2,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Typography variant='h6' sx={{ fontWeight: 'bold' }}>
							Sync Status
						</Typography>
						<IconButton
							size='medium'
							onClick={(e) => {
								e.stopPropagation()
								// TODO: Implement sync refresh
							}}
						>
							<RefreshIcon fontSize='medium' />
						</IconButton>
					</Box>
					<Divider />
					<Box sx={{ p: 2 }}>
						<Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 1 }}>
							Queued for Sync ({mutations?.length ?? 0})
						</Typography>
						{mutations?.length === 0 ? (
							<Typography variant='body2' color='text.secondary'>
								No pending syncs
							</Typography>
						) : (
							<List dense sx={{ p: 0 }}>
								{mutations?.map((mutation) => (
									<ListItem key={mutation.id} sx={{ px: 0, py: 0.5 }}>
										<ListItemIcon sx={{ minWidth: 32 }}>
											<PendingIcon fontSize='small' color='warning' />
										</ListItemIcon>
										<ListItemText
											primary={mutation.title}
											secondary={dayjs(mutation.createdAt).format('YYYY-MM-DD h:mm A')}
											primaryTypographyProps={{
												variant: 'body2',
												sx: { fontWeight: 'medium' },
											}}
											secondaryTypographyProps={{
												variant: 'caption',
												color: 'text.secondary',
											}}
										/>
									</ListItem>
								))}
							</List>
						)}
					</Box>
				</Paper>
			</Menu>
		</>
	)
}
